import { generateMetricKey, generateMetricRatioKey, getGranularityAUMetricKey } from '@intelligence/v3/utils/metric';
import { getComparePrevFieldKey } from '@intelligence/v3/utils/topcharts';
import { getFirstFiniteMetricValue } from '@intelligence/v3/utils/metricCompareUtils';

const AU_METRIC_STD_NAME_MAP: IRecord<string> = {
  dau: 'au',
  wau: 'au',
  mau: 'au',
};

interface IMetricCompareFieldKeys {
  metricStd: string;
  valueKey: string;
  ratioKey: string;
  prevValueKey: string;
  valueKeyCandidates: string[];
  ratioKeyCandidates: string[];
  prevValueKeyCandidates: string[];
}

const getUniqueValues = (values: string[]): string[] => {
  return values.filter((value, index) => !!value && values.indexOf(value) === index);
};

const getMetricFieldCandidates = (metric: string, granularity?: IGranularity): string[] => {
  const granularityMetric = (() => {
    if (!AU_METRIC_STD_NAME_MAP[metric]) {
      return '';
    }

    return getGranularityAUMetricKey(granularity || 'daily');
  })();
  const metricStd = AU_METRIC_STD_NAME_MAP[metric] || metric;

  return getUniqueValues([granularityMetric, metric, metricStd]);
};

const getMetricCompareFieldKeys = (
  dataSource: string,
  metric: string,
  granularity?: IGranularity,
): IMetricCompareFieldKeys => {
  const metricStd = AU_METRIC_STD_NAME_MAP[metric] || metric;
  const valueKeyCandidates = getMetricFieldCandidates(metric, granularity).map((metricField) => {
    return generateMetricKey({ dataSource, metric: metricField });
  });
  const ratioKeyCandidates = valueKeyCandidates.map((key) => {
    return generateMetricRatioKey(key);
  });
  const prevValueKeyCandidates = valueKeyCandidates.map((key) => {
    return getComparePrevFieldKey(key);
  });

  return {
    metricStd,
    valueKey: valueKeyCandidates[0] || '',
    ratioKey: ratioKeyCandidates[0] || '',
    prevValueKey: prevValueKeyCandidates[0] || '',
    valueKeyCandidates,
    ratioKeyCandidates,
    prevValueKeyCandidates,
  };
};

export const getMetricCompareValues = (
  record: IRecord | null | undefined,
  dataSource: string,
  metric: string,
  granularity?: IGranularity,
) => {
  const { metricStd, valueKeyCandidates, ratioKeyCandidates, prevValueKeyCandidates } = getMetricCompareFieldKeys(
    dataSource,
    metric,
    granularity,
  );

  return {
    metricStd,
    value: getFirstFiniteMetricValue(record, valueKeyCandidates),
    ratioValue: getFirstFiniteMetricValue(record, ratioKeyCandidates),
    prevValue: getFirstFiniteMetricValue(record, prevValueKeyCandidates),
  };
};
